import { Button, Menu } from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { useContext, useState } from "react";
import { ObjectName } from "../../../utils/enums";
import { ConnectionWizard } from "../../ConnectionWizard/ConnectionWizard";
import { ConsumerWizard } from "../../ConsumerWizard/ConsumerWizard";
import { GasConnectionWizard } from "../../GasConnectionWizard/GasConnectionWizard";
import { GeneratorWizard } from "../../GeneratorWizard/GeneratorWizard";
import { GhostNodeWizard } from "../../GhostNodeWizard/GhostNodeWizard";
import { MarketLocationWizard } from "../../MarketLocationWizard/MarketLocationWizard";
import { MeteringLocationWizard } from "../../MeteringLocationWizard/MeteringLocationWizard";
import { MeterWizard } from "../../MeterWizard/MeterWizard";
import { BatteryStorageModal } from "../../ObjectModals/BatteryStorageModal/BatteryStorageModal";
import { PersonWizard } from "../../PersonWizard/PersonWizard";
import { UserAccessContext } from "../UserAccessContext";
import "./CreateComponentDropdown.scss";

interface CreateComponentDropdownProps {
  projectId: string;
  siteId: number;
  variantId: number;
  hasGasConnection: boolean;
  graphCoordinates?: { x: number; y: number };
  setShowNotAccessibleModal?: (show: boolean) => void;
}

function CreateComponentDropdown({
  siteId,
  variantId,
  projectId,
  hasGasConnection,
  graphCoordinates,
  setShowNotAccessibleModal
}: CreateComponentDropdownProps) {
  const [modalObjectName, setModalObjectName] = useState<ObjectName | null>(
    null
  );
  const userCanCreateAndDelete = useContext(UserAccessContext);
  function handleCloseModal() {
    setModalObjectName(null);
  }

  function handleOpenModal(objectName: ObjectName) {
    if (
      userCanCreateAndDelete &&
      setShowNotAccessibleModal &&
      !userCanCreateAndDelete[objectName]
    ) {
      setShowNotAccessibleModal(true);
    } else {
      setModalObjectName(objectName);
    }
  }

  function isDisabled(objectName: ObjectName): boolean {
    return !!userCanCreateAndDelete && !userCanCreateAndDelete[objectName];
  }

  return (
    <div className="CreateComponentDropdown">
      <Menu>
        <Menu.Target>
          <Button rightSection={<IconCaretDownFilled size="1rem" />}>
            Neu erstellen
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            disabled={isDisabled(ObjectName.Generator)}
            onClick={() => handleOpenModal(ObjectName.Generator)}
          >
            {ObjectName.Generator}
          </Menu.Item>
          <Menu.Item
            disabled={isDisabled(ObjectName.Consumer)}
            onClick={() => handleOpenModal(ObjectName.Consumer)}
          >
            {ObjectName.Consumer}
          </Menu.Item>
          <Menu.Item
            disabled={isDisabled(ObjectName.Storage)}
            onClick={() => handleOpenModal(ObjectName.Storage)}
          >
            {ObjectName.Storage}
          </Menu.Item>
          <Menu.Item
            disabled={isDisabled(ObjectName.Connection)}
            onClick={() => handleOpenModal(ObjectName.Connection)}
          >
            {ObjectName.Connection}
          </Menu.Item>
          {!hasGasConnection && (
            <Menu.Item
              disabled={isDisabled(ObjectName.GasConnection)}
              onClick={() => handleOpenModal(ObjectName.GasConnection)}
            >
              {ObjectName.GasConnection}
            </Menu.Item>
          )}
          <Menu.Item
            disabled={isDisabled(ObjectName.Meter)}
            onClick={() => handleOpenModal(ObjectName.Meter)}
          >
            {ObjectName.Meter}
          </Menu.Item>
          <Menu.Item
            disabled={isDisabled(ObjectName.GhostNode)}
            onClick={() => handleOpenModal(ObjectName.GhostNode)}
          >
            {ObjectName.GhostNode}
          </Menu.Item>
          <Menu.Item
            disabled={isDisabled(ObjectName.MarketLocation)}
            onClick={() => handleOpenModal(ObjectName.MarketLocation)}
          >
            {ObjectName.MarketLocation}
          </Menu.Item>
          <Menu.Item
            disabled={isDisabled(ObjectName.MeteringLocation)}
            onClick={() => handleOpenModal(ObjectName.MeteringLocation)}
          >
            {ObjectName.MeteringLocation}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {modalObjectName === ObjectName.Storage && (
        <BatteryStorageModal
          graphCoordinates={graphCoordinates}
          isOpen
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.GhostNode && (
        <GhostNodeWizard
          graphCoordinates={graphCoordinates}
          isOpen
          mode="create"
          siteId={siteId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.Person && (
        <PersonWizard
          graphCoordinates={graphCoordinates}
          isOpen
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.Connection && (
        <ConnectionWizard
          graphCoordinates={graphCoordinates}
          isOpen
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.Consumer && (
        <ConsumerWizard
          graphCoordinates={graphCoordinates}
          isOpen
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.GasConnection && (
        <GasConnectionWizard
          graphCoordinates={graphCoordinates}
          isOpen
          mode="create"
          siteId={siteId}
          variantId={variantId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.Meter && (
        <MeterWizard
          graphCoordinates={graphCoordinates}
          isOpen
          mode="create"
          projectId={projectId}
          siteId={siteId}
          variantId={variantId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.MarketLocation && (
        <MarketLocationWizard
          isOpen
          mode="create"
          siteId={siteId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.MeteringLocation && (
        <MeteringLocationWizard
          isOpen
          mode="create"
          siteId={siteId}
          onClose={handleCloseModal}
        />
      )}
      {modalObjectName === ObjectName.Generator && (
        <GeneratorWizard
          graphCoordinates={graphCoordinates}
          isOpen
          siteId={siteId}
          variantId={variantId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export { CreateComponentDropdown };
